import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import OpeningScreen from "@/components/OpeningScreen";
import Simulation from "@/components/Simulation";
import Ending from "@/components/Ending";
import Debrief from "@/components/Debrief";
import PracticeReport from "@/components/PracticeReport";
import { useSound } from "@/context/SoundContext";
import { getGuardianId } from "@/lib/guardian";
import { track } from "@/lib/analytics";
import { EXPERIENCE_MODULES } from "@/simulations/registry";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SoundToggle = () => {
  const { enabled, toggle } = useSound();
  return (
    <button
      onClick={toggle}
      data-testid="sound-toggle"
      aria-label={enabled ? "Sound on" : "Sound off"}
      className="fixed right-5 top-6 z-50 flex h-10 w-10 items-center justify-center rounded-full border border-white/12 bg-black/30 text-white/80 backdrop-blur-xl transition-colors hover:bg-black/50 sm:right-7"
    >
      {enabled ? <Volume2 size={17} style={{ color: ACCENT }} /> : <VolumeX size={17} className="text-white/50" />}
    </button>
  );
};

// Multi-simulation dispatch: simulations registered in src/simulations/registry.js render their
// own experience module; everything else (sim_000001) uses the existing implementation below.
export default function SimulationExperience({ sim }) {
  const { stopAll } = useSound();
  // Shared container lifecycle: leaving any simulation route (Home, /simulations, browser Back, …)
  // unmounts this container → all simulation audio (ambient + narration) stops.
  useEffect(() => () => stopAll(), [stopAll]);
  const Dedicated = sim && EXPERIENCE_MODULES[sim.simulationId];
  if (Dedicated) return <Dedicated sim={sim} />;
  return <LegacySimulationExperience sim={sim} />;
}

function LegacySimulationExperience({ sim }) {
  const navigate = useNavigate();
  const { unlock, stopNarration } = useSound();
  const [phase, setPhase] = useState("opening"); // opening | sim | ending | debrief | parent
  const [transcript, setTranscript] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [reportEmail, setReportEmail] = useState("");
  const simId = (sim && sim.simulationId) || null;
  const simSlug = (sim && sim.slug) || null;
  const meta = (sid) => ({ simulation_id: simId, session_id: sid || sessionId || undefined });

  // Preserve existing analytics: fire simulation_page_view once when the simulation page opens.
  const pvRef = useRef(false);
  useEffect(() => {
    if (!pvRef.current) {
      pvRef.current = true;
      track("simulation_page_view", null, { simulation_id: simId });
    }
  }, [simId]);

  const goHome = () => { stopNarration(); navigate("/"); };
  const goToParent = () => { stopNarration(); track("parent_view", null, meta()); setPhase("parent"); };

  const startAttempt = (isReplay) => {
    unlock();
    const sid = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
    // Every genuine attempt (first BEGIN or Practice Again) = exactly one sim_start, so it
    // counts as +1 Simulation Start. Replay additionally emits simulation_restart (unchanged).
    track("sim_start", null, { simulation_id: simId, session_id: sid });
    if (isReplay) {
      track("simulation_restart", null, { simulation_id: simId, session_id: sid });
    }
    // Persist the canonical session the moment an attempt actually starts (not on page
    // visit). Idempotent create-once by session_id on the backend. simId/simSlug come
    // from the active simulation registry entry — no hardcoded simulation values.
    fetch(`${API}/simulation/start`, {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sid, simulation_id: simId, slug: simSlug, guardian_id: getGuardianId() }),
    }).catch(() => {});
    setSessionId(sid);
    setTranscript([]);
    setSummary(null);
    setPhase("sim");
  };
  const startSim = () => startAttempt(false);
  const restartSim = () => startAttempt(true);

  const onComplete = (finalTranscript) => {
    setTranscript(finalTranscript);
    const sid = sessionId;
    track("simulation_complete", null, { simulation_id: simId, session_id: sid });
    setPhase("ending");
    // Record the completion independently of summary success — this guarantees the
    // Admin "Simulation Completions" (session records) reconciles with the funnel
    // simulation_complete event at the same completion point.
    const completion = fetch(`${API}/simulation/record`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ guardian_id: getGuardianId(), session_id: sid, simulation_id: simId, slug: simSlug }),
    }).catch(() => {});
    // Generate the AI summary AFTER the completion write has settled, so the backend sees a
    // completed canonical session and can persist exactly one frozen Practice Report.
    completion.finally(() =>
    fetch(`${API}/simulation/summary`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transcript: finalTranscript, session_id: sid }),
    })
      .then((r) => r.json())
      .then((data) => {
        setSummary(data);
        // Attach capabilities to the SAME completion record (idempotent upsert by session_id).
        fetch(`${API}/simulation/record`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            guardian_id: getGuardianId(),
            session_id: sid,
            simulation_id: simId,
            slug: simSlug,
            capabilities: data.capabilities || [],
          }),
        }).catch(() => {});
      })
      .catch(() => {
        setSummary({
          child: {
            did_well: "You stayed with a really hard moment instead of walking away from it.",
            practise: "Putting how you feel into clear, calm words when something stings.",
            try_next: "Next time, you could tell one person you trust what happened.",
          },
          parent: {
            strengths: ["At the lunch table, your child stayed engaged rather than shutting down.", "When support was offered, they were open to responding."],
            worth_practising: ["Naming feelings calmly in the moment.", "Knowing when to ask an adult for help."],
            what_we_noticed: ["Across the scenes, your child navigated an uncomfortable social situation thoughtfully."],
            try_this_together: ["Ask: \"If you ever felt left out, what could I do that would actually help?\""],
          },
          capabilities: ["Staying calm under pressure", "Reaching out for support"],
        });
      }));
  };

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-[#0a0a0a]" data-testid="simulation-experience">
      <SoundToggle />
      <AnimatePresence mode="wait">
        {phase === "opening" && (
          <motion.div key="opening" exit={{ opacity: 0, scale: 1.04 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0">
            <OpeningScreen onEnter={startSim} onHome={goHome} />
          </motion.div>
        )}
        {phase === "sim" && (
          <motion.div key="sim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0">
            <Simulation onComplete={onComplete} onHome={goHome} simId={simId} sessionId={sessionId} />
          </motion.div>
        )}
        {phase === "ending" && (
          <motion.div key="ending" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0">
            <Ending onContinue={() => { stopNarration(); setPhase("debrief"); }} onHome={goHome} />
          </motion.div>
        )}
        {phase === "debrief" && (
          <motion.div key="debrief" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0">
            <Debrief summary={summary} onRestart={restartSim} onHome={goHome} onViewParent={goToParent} />
          </motion.div>
        )}
        {phase === "parent" && (
          <motion.div key="parent" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0 overflow-y-auto">
            <PracticeReport summary={summary} simTitle={(sim && sim.title) || null} onBack={() => setPhase("debrief")} onHome={goHome} onRestart={restartSim} simId={simId} sessionId={sessionId} prefillEmail={reportEmail} onEmailCaptured={setReportEmail} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
